import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import {
  addCrmLeadExternalId,
  searchCrmOrderGsts,
  searchCrmPmsgVendors,
} from '../config/api';

const TYPE_OPTIONS = [
  { value: 'gst', label: 'GST' },
  { value: 'pmsg_vendor_id', label: 'PMSG vendor' },
];

const CrmLinkIdDialog = ({ leadId, onClose, onSaved }) => {
  const [type, setType] = useState('gst');
  const [selectedOption, setSelectedOption] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [existingLeadId, setExistingLeadId] = useState(null);

  const clearError = () => {
    if (error) setError('');
    if (existingLeadId) setExistingLeadId(null);
  };

  const loadOptions = async (input) => {
    const search = input.trim();
    if (search.length < 2) return [];
    try {
      if (type === 'gst') {
        const response = await searchCrmOrderGsts(search);
        const result = response?.data || response;
        return (result?.gsts || []).map((item) => ({
          value: item.gst,
          label: item.companyName || item.gst,
          item,
        }));
      }

      const response = await searchCrmPmsgVendors(search);
      const result = response?.data || response;
      return (result?.vendors || []).map((vendor) => ({
        value: vendor.vendorId,
        label: vendor.vendorName || 'Unnamed PMSG vendor',
        item: vendor,
      }));
    } catch (err) {
      setError(err.message || 'Could not search identifiers.');
      return [];
    }
  };

  const save = async (event) => {
    event.preventDefault();
    if (!selectedOption) {
      setError(type === 'gst' ? 'Search for and select an order GST.' : 'Search for and select a PMSG vendor.');
      return;
    }

    setSaving(true);
    setError('');
    setExistingLeadId(null);
    try {
      await addCrmLeadExternalId(leadId, { type, value: selectedOption.value });
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      setError(err.message || 'Could not link the identifier.');
      setExistingLeadId(err.details?.leadId || null);
    } finally {
      setSaving(false);
    }
  };

  const formatOption = (option) => {
    const item = option.item;
    if (type === 'gst') {
      return (
        <div className="crm-oms-user-option">
          <strong>{option.label}</strong>
          <span>
            GST {item.gst} · {item.orderCount} order{item.orderCount === 1 ? '' : 's'}
            {item.linkedLeadId ? ` · Linked to Lead #${item.linkedLeadId}` : ''}
          </span>
        </div>
      );
    }
    return (
      <div className="crm-oms-user-option">
        <strong>{option.label}</strong>
        <span>
          {[item.state, `Vendor reference: ${item.vendorId}`].filter(Boolean).join(' · ')}
          {item.linkedLeadId ? ` · Linked to Lead #${item.linkedLeadId}` : ''}
        </span>
      </div>
    );
  };

  return (
    <div className="modal-overlay" onClick={saving ? undefined : onClose}>
      <div className="modal-content" onClick={(event) => event.stopPropagation()}>
        <form onSubmit={save}>
          <div className="modal-header">
            <h3>Link ID</h3>
            <button type="button" className="modal-close" onClick={onClose} disabled={saving}>×</button>
          </div>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label" htmlFor="crm-external-id-type">Identifier type</label>
              <select
                id="crm-external-id-type"
                className="form-control"
                value={type}
                onChange={(event) => {
                  setType(event.target.value);
                  setSelectedOption(null);
                  clearError();
                }}
                disabled={saving}
              >
                {TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="crm-link-id-search">
                {type === 'gst' ? 'OMS order GST' : 'PMSG vendor'}
              </label>
              <AsyncSelect
                key={type}
                inputId="crm-link-id-search"
                cacheOptions
                defaultOptions={false}
                loadOptions={loadOptions}
                value={selectedOption}
                onChange={(option) => {
                  setSelectedOption(option);
                  clearError();
                }}
                isOptionDisabled={(option) => !!option.item.linkedLeadId}
                formatOptionLabel={formatOption}
                placeholder={type === 'gst' ? 'Search GST or customer/company name' : 'Search vendor name'}
                noOptionsMessage={({ inputValue }) => (
                  inputValue.length < 2
                    ? 'Enter at least 2 characters'
                    : type === 'gst' ? 'No order GST found' : 'No PMSG vendor found'
                )}
                isClearable
                isDisabled={saving}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 1100 }) }}
                autoFocus
              />
              <div className="crm-form-help">
                {type === 'gst'
                  ? 'Orders with the selected GST will be shown against this Lead.'
                  : 'Vendor references come from the latest imported PMSG snapshot.'}
              </div>
            </div>

            {error && <div className="crm-form-error">{error}</div>}
            {existingLeadId && (
              <Link className="crm-existing-lead-link" to={`/crm/leads/${existingLeadId}`} onClick={onClose}>
                Open Lead #{existingLeadId}
              </Link>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving || !selectedOption}>
              {saving ? 'Linking…' : 'Link ID'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CrmLinkIdDialog;
